import React, { memo, useMemo, useRef, useEffect, useState } from 'react';
import { LineChart, PieChart, TableChart } from '@sto/sto-charts';
import { StoChartProps } from './types';

const StoChart: React.FC<StoChartProps> = ({ content: contentStr, type, question }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const chartRef = useRef(null);
  const containerWidthRef = useRef<number>(0); // 用于存储最新的宽度值

  // 监听容器宽度变化
  useEffect(() => {
    // 使用 ResizeObserver 监听 scroll-container 的尺寸变化
    let resizeObserver: ResizeObserver | null = null;
    const scrollContainer = document.getElementById('scroll-container');
    
    if (scrollContainer && window.ResizeObserver) {
      try {
        resizeObserver = new ResizeObserver((entries) => {
          // 使用 requestAnimationFrame 来避免在 ResizeObserver 回调中同步修改 DOM
          requestAnimationFrame(() => {
            try {
              for (const entry of entries) {
                if (entry.target === scrollContainer) {
                  const newWidth = entry.contentRect.width - 20;
                  // 只有当宽度真正发生变化时才更新状态
                  if (newWidth !== containerWidthRef.current && newWidth > 0) {
                    containerWidthRef.current = newWidth;
                    setContainerWidth(newWidth);
                    
                    // 改变图表尺寸，在容器大小发生改变时需要手动调用
                    if (chartRef.current) {
                      chartRef.current.getMyChart()?.resize();
                    }
                  }
                }
              }
            } catch (error) {
              console.warn('ResizeObserver callback error:', error);
            }
          });
        });
        resizeObserver.observe(scrollContainer);
      } catch (error) {
        console.warn('ResizeObserver creation error:', error);
      }
    }
    
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // 判断字符串是否为完整 JSON
  const isLikelyCompleteJson = (str: string) => {
    if (!str) return false;
    const trimmed = str.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        JSON.parse(trimmed);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  };

  const content = useMemo(() => {
    if (typeof contentStr === 'string') {
      if (isLikelyCompleteJson(contentStr)) {
        try {
          return JSON.parse(contentStr);
        } catch {
          return contentStr;
        }
      }
      return contentStr;
    } else {
      return contentStr || {};
    }
  }, [contentStr]);

  // 使用 useMemo 优化默认配置计算，排除 dataSource/resultList 避免覆盖 safeDataSource
  const defaultProps = useMemo(() => {
    const { dataSource: _ds, resultList: _rl, ...chartProps } = content?.chartProps || {};
    return {
      height: content?.height || 300,
      width: containerWidth > 0 ? containerWidth : undefined,
      loading: content?.loading || false,
      style: { 
        marginTop: '12px', 
        width: containerWidth > 0 ? `${containerWidth}px` : '100%'
      },
      showLabelValue: true,
      ...chartProps,
      ref: chartRef
    };
  }, [content, containerWidth]);

  // 根据类型渲染不同的图表
  const renderChart = () => {
    const { mapConfig } = content || {};
    // 确保 dataSource 始终为数组，避免 @sto/sto-charts 内部 resultList.slice 报错
    const safeDataSource = Array.isArray(content?.dataSource) ? content.dataSource : [];
    const { dataSource: _ds, resultList: _rl, ...restContent } = content || {};
    // 处理x轴和y轴的类型返回null的情况，不然页面会报错
    if (mapConfig?.xAxis) {
      mapConfig.xAxis.type = mapConfig.xAxis.type ? mapConfig.xAxis.type :'category';
    }
    // @sto/sto-charts 的 getYAxis 要求 yAxis 为数组，单对象需转为数组避免 resultList.slice 报错
    if (mapConfig?.yAxis) {
      if (Array.isArray(mapConfig.yAxis)) {
        mapConfig.yAxis.forEach(item => {
          item.type = item.type ? item.type : 'value';
        });
      } else {
        mapConfig.yAxis = [{
          ...mapConfig.yAxis,
          type: mapConfig.yAxis.type || 'value',
        }];
      }
    }
    switch (type) {
      case 'LINE_CHART':
        return (
          <LineChart
            {...defaultProps}
            {...restContent}
            dataSource={safeDataSource}
            resultList={safeDataSource}
            mapConfig={{
              ...mapConfig,
              labelLayout: {
                hideOverlap: true, // 是否隐藏重叠的标签
              },
            }}
          />
        );

      case 'BAR_CHART':
        return (
          <LineChart
            {...defaultProps}
            {...restContent}
            dataSource={safeDataSource}
            resultList={safeDataSource}
            mapConfig={{
              ...mapConfig,
              series: mapConfig?.series?.map(item => ({
                ...item,
                type: 'bar' // 设置为柱状图
              })) || []
            }}
            hideCheckbox={content.hideCheckbox}
            tooltipTheme={content.tooltipTheme || 'light'}
            legendPosition={content.legendPosition}
            showLabelValue={content.showLabelValue}
          />
        );

      case 'PIE_CHART':
        return (
          <PieChart
            dataSource={safeDataSource}
            mapConfig={{
              ...mapConfig,
              series: mapConfig?.series?.map(item => ({
                ...item,
                type: 'pie'
              })) || []
            }}
            {...defaultProps}
          />
        );

      case 'DOUGHNUT_CHART':
        return (
          <PieChart
            dataSource={safeDataSource}
            mapConfig={{
              ...mapConfig,
              series: mapConfig?.series?.map(item => ({
                ...item,
                type: 'cycle' // 环形图
              })) || []
            }}
            {...defaultProps}
          />
        );

      case 'TABLE_CHART':
        return (
          <TableChart
            dataSource={safeDataSource}
            columns={content.columns || []}
            {...defaultProps}
            style={{ marginTop: '12px' }} // 表格不需要最小高度
          />
        );

      default:
        console.warn(`Unsupported chart type: ${type}`);
        return <div>不支持的图表类型: {type}</div>;
    }
  };

  // 错误边界处理
  try {
    return (
      <div 
        ref={containerRef}
        className="sto-chart-container" 
        style={{ 
          width: containerWidth > 0 ? `${containerWidth}px` : 'auto',
          margin: '12px 0'
        }}
      >
        {renderChart()}
      </div>
    );
  } catch (error) {
    console.error(`Error rendering chart ${type}:`, error);
    return (
      <div style={{ 
        padding: '20px', 
        border: '1px solid #f0f0f0', 
        borderRadius: '4px',
        backgroundColor: '#fafafa',
        textAlign: 'center' as const,
        color: '#999'
      }}>
        图表渲染出错，请检查数据格式
      </div>
    );
  }
};

export default memo(StoChart); 
